/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-empty-function */
import { StateCreator } from 'zustand';
import { Dispatch } from 'react';
import { produce } from 'immer';
import { createAppStore } from '@marriott/mi-store-utils';

// Import types from the existing SearchResults store
import { SearchStateStoreDataType, UpdateSearchStateActionType } from '../../organisms/SearchResults/store/store.type';
import { getInitialSearchMockState } from '../../organisms/SearchResults/store/store.action';
import { UpdateSearchState } from '../../organisms/SearchResults/store/store.reducer';
import { hardCodedAllLocale } from '../../lib/organisms/SearchForm/component/index.constants';

/**
 * Interface for the SearchFilter Context type
 * This matches the original context structure for seamless migration
 */
export interface SearchFilterContextType {
  searchQueryState: SearchStateStoreDataType;
  setSearchQuery: Dispatch<UpdateSearchStateActionType>;
}

/**
 * Interface for the SearchFilter Zustand store
 * Matches SearchFilterContextType exactly to maintain API compatibility
 * Plus adds direct methods for each SearchAction
 */
export interface SearchFilterStore extends SearchFilterContextType {
  // State - matches the context structure
  searchQueryState: SearchStateStoreDataType;

  // Actions - dispatch function that matches the reducer pattern
  setSearchQuery: Dispatch<UpdateSearchStateActionType>;

  // Individual setter methods for independent store updates
  setSearchQueryState: (searchQueryState: SearchStateStoreDataType) => void;

  // Direct methods for each SearchAction (replacing reducer pattern)
  setQuery: (payload: any) => void;
  setFilter: (payload: any) => void;
  setApplyFilter: (payload: any) => void;
  updateOtherSearchQueryParam: (payload: any) => void;
  updatePoints: (payload: any) => void;
  updateTaxesAndAvailability: (payload: any) => void;
  updateCurrency: (payload: any) => void;
  updateAmenities: (payload: any) => void;
  updateActivities: (payload: any) => void;
  updateBrands: (payload: any) => void;
  updateTransportationTypes: (payload: any) => void;
  updateQueryAndOtherSearchQueryParam: (payload: any) => void;
  updateMedia: (payload: any) => void;

  // Store initialization method (following searchFormStore pattern)
  searchFilterInitialization: (initialSearchState?: SearchStateStoreDataType) => void;

  // Store management methods
  resetStore: () => void;
}

/**
 * Initial state for the SearchFilter store
 */
const createInitialState = (): Omit<
  SearchFilterStore,
  | 'setSearchQuery'
  | 'setSearchQueryState'
  | 'searchFilterInitialization'
  | 'resetStore'
  | 'setQuery'
  | 'setFilter'
  | 'setApplyFilter'
  | 'updateOtherSearchQueryParam'
  | 'updatePoints'
  | 'updateTaxesAndAvailability'
  | 'updateCurrency'
  | 'updateAmenities'
  | 'updateActivities'
  | 'updateBrands'
  | 'updateTransportationTypes'
  | 'updateQueryAndOtherSearchQueryParam'
  | 'updateMedia'
> => ({
  searchQueryState: getInitialSearchMockState({}, false, hardCodedAllLocale.en, '', false, '', false),
});

/**
 * Reducer function that handles SearchFilter actions
 * Uses the existing UpdateSearchState reducer from the SearchResults store
 */
const searchFilterReducer = (
  state: SearchStateStoreDataType,
  action: UpdateSearchStateActionType
): SearchStateStoreDataType => {
  // Use the existing reducer logic from the SearchResults store
  return UpdateSearchState(state, action);
};

/**
 * SearchFilter Zustand store creator
 * Maintains the same API as the original Context for seamless migration
 */
const SearchFilterStoreCreator: StateCreator<SearchFilterStore> = set => {
  return {
    ...createInitialState(),

    // setSearchQuery function that replicates the original reducer logic
    setSearchQuery: (action: UpdateSearchStateActionType) => {
      set(
        produce((state: SearchFilterStore) => {
          // Apply the reducer logic to update the searchQueryState
          state.searchQueryState = searchFilterReducer(state.searchQueryState, action);
        })
      );
    },

    // Individual setter methods for independent store updates
    setSearchQueryState: (searchQueryState: SearchStateStoreDataType) => {
      set(
        produce((state: SearchFilterStore) => {
          state.searchQueryState = { ...searchQueryState };
        })
      );
    },

    // Direct methods for each SearchAction (replacing reducer pattern)
    // These methods directly update specific parts of the searchQueryState

    setQuery: (payload: any) => {
      set(
        produce((state: SearchFilterStore) => {
          // setQuery updates the search property within searchQueryState
          // Ensure dates in options remain as strings
          const updatedSearch = {
            ...state.searchQueryState.search,
            ...payload,
          };

          // If payload contains options with dates, ensure they're strings
          if (updatedSearch.options) {
            if (updatedSearch.options.startDate && typeof updatedSearch.options.startDate !== 'string') {
              updatedSearch.options.startDate = String(updatedSearch.options.startDate);
            }
            if (updatedSearch.options.endDate && typeof updatedSearch.options.endDate !== 'string') {
              updatedSearch.options.endDate = String(updatedSearch.options.endDate);
            }
          }

          state.searchQueryState.search = updatedSearch;
        })
      );
    },

    setFilter: (payload: any) => {
      set(
        produce((state: SearchFilterStore) => {
          state.searchQueryState.filter = payload;
        })
      );
    },

    setApplyFilter: (payload: any) => {
      set(
        produce((state: SearchFilterStore) => {
          Object.assign(state.searchQueryState, payload);
        })
      );
    },

    updateOtherSearchQueryParam: (payload: any) => {
      set(
        produce((state: SearchFilterStore) => {
          Object.assign(state.searchQueryState, payload);
        })
      );
    },

    updatePoints: (payload: any) => {
      set(
        produce((state: SearchFilterStore) => {
          Object.assign(state.searchQueryState, payload);
        })
      );
    },

    updateTaxesAndAvailability: (payload: any) => {
      set(
        produce((state: SearchFilterStore) => {
          Object.assign(state.searchQueryState, payload);
        })
      );
    },

    updateCurrency: (payload: any) => {
      set(
        produce((state: SearchFilterStore) => {
          Object.assign(state.searchQueryState, payload);
        })
      );
    },

    updateAmenities: (payload: any) => {
      set(
        produce((state: SearchFilterStore) => {
          Object.assign(state.searchQueryState, payload);
        })
      );
    },

    updateActivities: (payload: any) => {
      set(
        produce((state: SearchFilterStore) => {
          Object.assign(state.searchQueryState, payload);
        })
      );
    },

    updateBrands: (payload: any) => {
      set(
        produce((state: SearchFilterStore) => {
          Object.assign(state.searchQueryState, payload);
        })
      );
    },

    updateTransportationTypes: (payload: any) => {
      set(
        produce((state: SearchFilterStore) => {
          Object.assign(state.searchQueryState, payload);
        })
      );
    },

    updateQueryAndOtherSearchQueryParam: (payload: any) => {
      set(
        produce((state: SearchFilterStore) => {
          Object.assign(state.searchQueryState, payload);
        })
      );
    },

    updateMedia: (payload: any) => {
      set(
        produce((state: SearchFilterStore) => {
          Object.assign(state.searchQueryState, payload);
        })
      );
    },

    // Store initialization method (following searchFormStore pattern)
    searchFilterInitialization: (initialSearchState?: SearchStateStoreDataType) => {
      set(
        produce((state: SearchFilterStore) => {
          if (initialSearchState) {
            // Deep copy the initial state to ensure new references
            const newState = { ...initialSearchState };

            // Deep copy the search object to ensure new reference for useEffect dependencies
            if (newState.search) {
              newState.search = {
                ...newState.search,
                options: newState.search.options ? { ...newState.search.options } : undefined,
              };

              // Ensure dates in search.options are properly formatted as strings
              if (newState.search.options) {
                const options = newState.search.options;

                // Ensure startDate is a string in YYYY-MM-DD format
                if (options.startDate) {
                  if (typeof options.startDate !== 'string') {
                    // Convert to string if it's not already
                    options.startDate = String(options.startDate);
                  }
                }

                // Ensure endDate is a string in YYYY-MM-DD format
                if (options.endDate) {
                  if (typeof options.endDate !== 'string') {
                    // Convert to string if it's not already
                    options.endDate = String(options.endDate);
                  }
                }
              }
            }

            state.searchQueryState = newState;
          } else {
            // Reset to default values
            const initialState = createInitialState();
            state.searchQueryState = { ...initialState.searchQueryState };
          }
        })
      );
    },

    resetStore: () => {
      set(
        produce((state: SearchFilterStore) => {
          const initialState = createInitialState();
          Object.assign(state, initialState);
        })
      );
    },
  };
};

/**
 * Create the SearchFilter store instance
 * Global singleton pattern as requested
 */
export const createSearchFilterStore = () => {
  return createAppStore(SearchFilterStoreCreator, {
    usePersistentStore: false,
  });
};

/**
 * Default store instance - single global instance (no componentId registry)
 */
export const useSearchFilterStore = createSearchFilterStore();
